import type {ViewableFile} from 'src/typings';
import type {FinishedResponseInteractionService} from 'shared/services';
import type {GridFieldValue, RecordRow} from 'src/features/record-grid/lib/types';
import type {AllDayReportsHoursSum} from './types';
import type {FileService} from 'src/features/file/file.service';
import type {ReportDetailDataService} from './report-detail.data-service';
import type RecordDataService from 'src/features/record/record.data-service';
import {hasOriginRecordReference} from 'src/features/record-grid/lib/types-utils';
import {exists} from 'shared/utils/custom-utils';
import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import {getDateFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
import {DateTime} from 'luxon';
import {FOND_REPORT} from 'src/features/erp-sutor/sutor-fonds';

export class ReportDetailService {
    public static serviceName = 'reportDetailService';

    /*@ngInject*/
    constructor(private recordDataService: RecordDataService,
                private reportDetailDataService: ReportDetailDataService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private fileService: FileService) {
    }

    public async getReportReferencePdfFile(field: GridFieldValue | null): Promise<ViewableFile> {
        if (!exists(field) || !hasOriginRecordReference(field)) {
            return null;
        }

        const referenceRecord = await this.recordDataService.getById(field.originRecordReference.id);
        if (!exists(referenceRecord)) {
            return null;
        }

        const allFiles = await this.fileService.getFilesByDirectory(referenceRecord.directoryId);
        return allFiles[0] ?? null;
    }

    public async getAllDayReportsHoursSum(dayReport: RecordRow): Promise<AllDayReportsHoursSum | null> {
        const workerField = getFirstFieldByFieldTypeIdRecursive(dayReport, FOND_REPORT.workerFieldId);
        const dateField = getFirstFieldByFieldTypeIdRecursive(dayReport, FOND_REPORT.dateFieldId);
        const date = getDateFromGridFieldValue(dateField);

        if (!hasOriginRecordReference(workerField) || !exists(date)) {
            return null;
        }

        const localDate = DateTime.now();
        const isoDateWithOffset = `${date.toISODate()}@${localDate.zoneName}`;

        try {
            return await this.reportDetailDataService.getAllDayReportsHoursSum(isoDateWithOffset, workerField.originRecordReference.id);
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return null;
        }
    }
}